import type { FailedImportRow, ImportEditionRow } from '@api/editions-api';
import { escapeCsvField, parseCsvHeaders, splitCsvLine } from '@utils/csv-utils';

export { escapeCsvField };

const REQUIRED_HEADERS = [
  'edition_external_id',
  'country_code',
  'edition_name',
  'year',
  'category_external_id',
  'participant_name',
] as const;

/**
 * The nominee key. Which one a file carries depends on its site: an SSO site
 * identifies nominees by Docplanner doctor id, a PMS site by HubSpot id. One of
 * the two must be present; the backend rejects the wrong one for the site.
 */
const KEY_HEADERS = ['dp_doctor_id', 'hsid'] as const;

export interface EditionPreview {
  editionExternalId: number;
  countryCode: string;
  /** Undefined when the file names no site, meaning the country's SSO site. */
  siteCode: string | undefined;
  name: string;
  year: number;
  categoryCount: number;
  participantCount: number;
  nomineeCount: number;
}

export function parseEditionRows(content: string): ImportEditionRow[] {
  const lines = content.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) return [];

  const { headers, delimiter } = parseCsvHeaders(lines[0]!);
  const headerIndex = new Map(headers.map((h, i) => [h, i]));

  const getValue = (fields: string[], header: string): string =>
    fields[headerIndex.get(header) ?? -1]?.trim() ?? '';

  return lines.slice(1).reduce<ImportEditionRow[]>((acc, line) => {
    const fields = splitCsvLine(line, delimiter);

    const editionExternalId = Number(getValue(fields, 'edition_external_id'));
    const countryCode = getValue(fields, 'country_code');
    const siteCode = getValue(fields, 'site_code');
    const editionName = getValue(fields, 'edition_name');
    const year = Number(getValue(fields, 'year'));
    const categoryExternalId = Number(getValue(fields, 'category_external_id'));
    const dpDoctorIdRaw = getValue(fields, 'dp_doctor_id');
    const hsid = getValue(fields, 'hsid');
    const participantName = getValue(fields, 'participant_name');
    const participantSurname = getValue(fields, 'participant_surname');
    const photoUrl = getValue(fields, 'photo_url');
    const profileUrl = getValue(fields, 'profile_url');
    const city = getValue(fields, 'city');

    if (!Number.isFinite(editionExternalId) || editionExternalId <= 0) return acc;
    if (!countryCode || !editionName || !Number.isFinite(year) || year < 1900) return acc;
    if (!Number.isFinite(categoryExternalId) || categoryExternalId <= 0) return acc;
    if (!participantName) return acc;

    // An unparseable dp_doctor_id is dropped rather than sent: the endpoint types it
    // as an integer, so a NaN would be serialised as null and reject the whole
    // request instead of the row. An hsid travels as text, so a malformed one is
    // sent and reported per row.
    const dpDoctorIdCandidate = Number(dpDoctorIdRaw);
    const dpDoctorId =
      dpDoctorIdRaw && Number.isInteger(dpDoctorIdCandidate) && dpDoctorIdCandidate > 0
        ? dpDoctorIdCandidate
        : undefined;
    if (dpDoctorId === undefined && !hsid) return acc;

    acc.push({
      editionExternalId,
      countryCode,
      ...(siteCode && { siteCode }),
      editionName,
      year,
      categoryExternalId,
      ...(dpDoctorId !== undefined && { dpDoctorId }),
      ...(hsid && { hsid }),
      participantName,
      ...(participantSurname && { participantSurname }),
      ...(photoUrl && { photoUrl }),
      ...(profileUrl && { profileUrl }),
      ...(city && { city }),
    });
    return acc;
  }, []);
}

/** The nominee key a row carries, whichever column it came from. */
function nomineeKeyOf(row: ImportEditionRow): string {
  return row.hsid ?? String(row.dpDoctorId);
}

export function getEditionPreviews(rows: ImportEditionRow[]): EditionPreview[] {
  const editionMap = new Map<number, { rows: ImportEditionRow[] }>();

  for (const row of rows) {
    const existing = editionMap.get(row.editionExternalId);
    if (existing) {
      existing.rows.push(row);
    } else {
      editionMap.set(row.editionExternalId, { rows: [row] });
    }
  }

  return [...editionMap.values()].map(({ rows: editionRows }) => {
    const first = editionRows[0]!;
    const categoryIds = new Set(editionRows.map((r) => r.categoryExternalId));
    const participants = new Set(editionRows.map(nomineeKeyOf));
    return {
      editionExternalId: first.editionExternalId,
      countryCode: first.countryCode,
      siteCode: first.siteCode,
      name: first.editionName,
      year: first.year,
      categoryCount: categoryIds.size,
      participantCount: participants.size,
      nomineeCount: editionRows.length,
    };
  });
}

const CSV_HEADERS = [
  'edition_external_id',
  'country_code',
  'site_code',
  'edition_name',
  'year',
  'category_external_id',
  'dp_doctor_id',
  'hsid',
  'participant_name',
  'participant_surname',
  'photo_url',
  'profile_url',
  'city',
  'error_reason',
] as const;

export function buildFailedRowsCsv(failedRows: FailedImportRow[]): string {
  const header = CSV_HEADERS.join(',');
  const lines = failedRows.map(({ row, reason }) =>
    [
      String(row.editionExternalId),
      escapeCsvField(row.countryCode),
      escapeCsvField(row.siteCode ?? ''),
      escapeCsvField(row.editionName),
      String(row.year),
      String(row.categoryExternalId),
      row.dpDoctorId === undefined ? '' : String(row.dpDoctorId),
      escapeCsvField(row.hsid ?? ''),
      escapeCsvField(row.participantName),
      escapeCsvField(row.participantSurname ?? ''),
      escapeCsvField(row.photoUrl ?? ''),
      escapeCsvField(row.profileUrl ?? ''),
      escapeCsvField(row.city ?? ''),
      escapeCsvField(reason),
    ].join(','),
  );

  return [header, ...lines].join('\n');
}

export function validateEditionHeaders(content: string): string | null {
  const lines = content.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) return 'The file must have a header row and at least one data row.';

  const { headers } = parseCsvHeaders(lines[0]!);

  for (const expected of REQUIRED_HEADERS) {
    if (!headers.includes(expected)) {
      return `Missing required column: "${expected}"`;
    }
  }

  if (!KEY_HEADERS.some((header) => headers.includes(header))) {
    return 'The file must identify nominees with a "dp_doctor_id" column (Doctoralia) or an "hsid" column (PMS).';
  }

  return null;
}
